import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import { useServices } from '../api/services';
import type {
  InvoiceDetail,
  InvoiceReportParams,
  InvoiceReportResponse,
  InvoiceRow,
  PaymentMethodOption,
  TransactionRequestBody,
  TransactionResult,
} from '../api/services/interfaces/InvoiceService';

// Minimal banner-facing shape (D-40). Backed by the consumer invoice report
// endpoint with server-side balance_due=true filtering. Phase 5 may extend
// the store with the full invoice lifecycle (pay, refund, dispute) and
// richer view models.
export interface UnpaidInvoice {
  id: number;
  amount_cents: number;
}

export type ReportStatus = 'idle' | 'loading' | 'success' | 'error';

// The detail surface (currentInvoice / fetchInvoice / clearCurrent) reuses the
// same four-state status union as the report surface (mirrors the quotes store's
// `DetailStatus`). Aliased for call-site clarity at the detail refs below.
export type DetailStatus = ReportStatus;

// eslint-disable-next-line max-lines-per-function -- defineStore setup aggregates four related surfaces (full report + banner shortcut + single-invoice detail + pay-modal payment actions, Plan 05.3-02); splitting would scatter related state across stores.
export const useInvoicesStore = defineStore('invoices', () => {
  // Full report (all invoices, no server-side filter) for nav badges and
  // anywhere that needs paid+unpaid mix. Populated by fetchReport(); the
  // banner-only fetchUnpaidInvoices() path below is independent and stays
  // server-filtered so it stays cheap.
  const report = ref<InvoiceRow[]>([]);
  const status = ref<ReportStatus>('idle');
  const fetchedAt = ref<number | null>(null);
  const error = ref<unknown>(null);

  // --- Single-invoice detail surface (Plan 05.2-02 D-19) ---
  // Co-located with the report surface (one store owns the invoice resource).
  // Deliberately independent of the report/status/fetchedAt state above: the
  // detail view (Plan 04) drives these via fetchInvoice/clearCurrent and the
  // report badges are never touched by a detail fetch.
  const currentInvoice = ref<InvoiceDetail | null>(null);
  const detailStatus = ref<DetailStatus>('idle');
  const detailError = ref<unknown>(null);

  // --- Pay-modal payment surface (Plan 05.3-02 D-11/D-12) ---
  // Co-located here (D-12: no new store carved). `paymentMethods` is the
  // authoritative customer method list + per-key SDK creds the modal needs
  // before ANYTHING renders (D-11); `paymentMethodsStatus` mirrors the same
  // four-state union as the report/detail surfaces. `isSubmitting` is a
  // double-submit lock (the server `next_payment_key` is the real idempotency
  // guard — T-05.3-02); `submitError` surfaces a usable `.message` from either
  // error envelope.
  const paymentMethods = ref<PaymentMethodOption[]>([]);
  const paymentMethodsStatus = ref<DetailStatus>('idle');
  const paymentMethodsError = ref<unknown>(null);
  const isSubmitting = ref(false);
  const submitError = ref<unknown>(null);

  // Badge-driving selectors. Hide-when-0 lives at the render site; this just
  // exposes the numbers.
  //
  // IN-04: these counts derive purely from `balanceCents > 0` over the full
  // report and are correct ONLY because the consumer report's `as_revenue`
  // server scope already excludes cancelled/voided invoices
  // (../api/app/models/ar_invoice.rb). If that scope ever stops filtering them,
  // a voided invoice with a positive balance would silently inflate the unpaid
  // badge here — revisit these selectors alongside any such backend change.
  const unpaidCount = computed(
    () => report.value.filter((row) => row.balanceCents > 0).length,
  );
  const unpaidTotalCents = computed(() =>
    report.value.reduce(
      (sum, row) => (row.balanceCents > 0 ? sum + row.balanceCents : sum),
      0,
    ),
  );
  // Past-due is an SPB-only concept. Non-SPB invoices stay "Open" indefinitely
  // (per sketch 021 README). Red-dot logic on the badge keys off this flag.
  const hasPastDue = computed(() =>
    report.value.some(
      (row) => row.progressBilling?.paymentStatus === 'past_due',
    ),
  );

  async function fetchReport(): Promise<void> {
    status.value = 'loading';
    error.value = null;
    try {
      const { invoices } = useServices();
      // per_page=200 covers the long tail. A typical customer has well under
      // 50 lifetime invoices (per sketch 021 estimate); no need to paginate
      // for the badge use case.
      const response = await invoices.fetchInvoiceReport({
        page: 0,
        rows: 200,
        sortField: 'id',
        sortOrder: -1,
      });
      report.value = response.data;
      status.value = 'success';
      fetchedAt.value = Date.now();
    } catch (err) {
      status.value = 'error';
      error.value = err;
      // Don't clear existing report data on transient failure. Keep the last
      // successful payload visible so badges don't flicker to empty.
    }
  }

  function invalidate(): void {
    report.value = [];
    status.value = 'idle';
    fetchedAt.value = null;
    error.value = null;
  }

  // quick-260527-njp: full per-account clear on logout / session expiry.
  // Identical contract to invalidate(); delegated to avoid duplication.
  function reset(): void {
    invalidate();
  }

  async function fetchUnpaidInvoices(): Promise<UnpaidInvoice[]> {
    try {
      const { invoices } = useServices();
      const response = await invoices.fetchInvoiceReport({
        page: 0,
        rows: 10,
        sortField: 'id',
        sortOrder: -1,
        balanceDue: true,
      });
      return response.data.map((row) => ({
        id: row.id,
        amount_cents: row.balanceCents,
      }));
    } catch {
      // Silent failure: the banner is a soft surface. Errors are still
      // captured by the axios interceptor (Sentry), so no toast here.
      return [];
    }
  }

  // Paginated history-zone fetch for the InvoicesView Payment History list
  // (Plan 04). Thin pass-through that threads the caller's AbortSignal so the
  // view's stale-guard (D-12) can cancel an in-flight request. Deliberately
  // does NOT touch the badge-driving report/status state above — that is
  // fetchReport's surface. Errors propagate; the view owns the requestId
  // stale-guard and the axios.isCancel swallow.
  async function fetchInvoicePage(
    params: InvoiceReportParams,
  ): Promise<InvoiceReportResponse> {
    const { invoices } = useServices();
    return invoices.fetchInvoiceReport(params);
  }

  // D-23 stale-guard: a monotonic counter bumped per fetchInvoice call. Only
  // the LATEST request is allowed to commit the shared `currentInvoice`
  // singleton + detailStatus/detailError. A response that arrives after a newer
  // navigation has superseded it returns/rethrows WITHOUT clobbering state, so
  // any consumer reading `store.currentInvoice` (now or future) sees the
  // winning invoice — closing the WR-01 stale-singleton-overwrite gap.
  let detailRequestId = 0;

  /**
   * Plan 05.2-02 (D-19) — fetch a single invoice detail for the /invoices/:id
   * view. Mirrors useQuotesStore.fetchQuote.
   *
   * WR-05 race-fix rule: callers MUST bind the awaited return value, NOT
   * re-read `store.currentInvoice` after the await — a concurrent navigation
   * can mutate the singleton between resolve and read. The action assigns the
   * singleton AND returns the value so both surfaces work.
   *
   * WR-01 (D-23): an internal `detailRequestId` counter makes the singleton
   * authoritative — a superseded response never overwrites `currentInvoice` /
   * detailStatus / detailError. The optional `signal` is forwarded to the
   * transport so the caller (InvoiceDetailView) can abort an in-flight request
   * on retry / route-change.
   *
   * WR-02: a cancellation (`axios.isCancel`) is NOT recorded as a real error —
   * the superseding request owns the state, so the cancelled request leaves
   * detailStatus/detailError untouched and simply rethrows for the caller's
   * `axios.isCancel` swallow.
   *
   * On a genuine (non-cancel, non-superseded) failure: sets
   * `detailStatus = 'error'`, `detailError = err`, and explicitly clears
   * `currentInvoice = null` (the detail view's error branch keys off
   * `!currentInvoice`), then rethrows so the view's Try-again card can react.
   * Unlike fetchReport (which preserves last-known report to keep badges
   * stable), clearing the singleton here is correct.
   */
  async function fetchInvoice(
    id: number,
    signal?: AbortSignal,
  ): Promise<InvoiceDetail> {
    const myId = ++detailRequestId;
    detailStatus.value = 'loading';
    detailError.value = null;
    try {
      const { invoices } = useServices();
      const data = await invoices.getInvoice(id, signal);
      // Superseded by a newer fetch — return the value (the caller binds it)
      // but do NOT clobber the singleton the winning request owns.
      if (myId !== detailRequestId) return data;
      currentInvoice.value = data;
      detailStatus.value = 'success';
      return data;
    } catch (err) {
      // WR-02: a cancellation is not a real error — leave state to the
      // superseding request and just rethrow for the caller's isCancel swallow.
      if (axios.isCancel(err)) throw err;
      // Superseded by a newer fetch — leave state alone and rethrow.
      if (myId !== detailRequestId) throw err;
      detailStatus.value = 'error';
      detailError.value = err;
      currentInvoice.value = null;
      throw err;
    }
  }

  /**
   * Plan 05.2-02 — reset ONLY the detail refs (route-leave on the detail view).
   * The report/status/fetchedAt/error surface above is deliberately untouched
   * so nav badges survive a detail-view visit.
   */
  function clearCurrent(): void {
    currentInvoice.value = null;
    detailStatus.value = 'idle';
    detailError.value = null;
  }

  // D-11 stale-guard: a monotonic counter bumped per fetchPaymentMethods call,
  // mirroring the fetchInvoice detail guard — only the LATEST request commits
  // `paymentMethods` / `paymentMethodsStatus`. A superseded slow response leaves
  // state to the winning request; an axios cancellation re-throws WITHOUT
  // recording an error (the modal re-opens supersede a prior fetch).
  let paymentMethodsRequestId = 0;

  /**
   * Plan 05.3-02 (D-11) — fetch the authoritative customer payment-method list +
   * per-key SDK creds for the pay modal. Nothing in the modal renders until this
   * resolves. Reuses the exact requestId + axios.isCancel + AbortController
   * stale-guard shape from fetchInvoice. On success stores the
   * PaymentMethodOption[]; on a genuine failure sets status=error + error; a
   * cancellation leaves state untouched and re-throws for the caller's
   * `axios.isCancel` swallow.
   */
  async function fetchPaymentMethods(
    signal?: AbortSignal,
    payToken?: string,
  ): Promise<PaymentMethodOption[]> {
    const myId = ++paymentMethodsRequestId;
    paymentMethodsStatus.value = 'loading';
    paymentMethodsError.value = null;
    try {
      const { invoices } = useServices();
      // Plan 09-05 reuse seam: an optional `payToken` routes to the Plan-02
      // pay-scoped sibling (payClient + ?temp_token=); absent, the authed
      // apiClient path is byte-unchanged (regression firewall, Pitfall 3).
      const methods = payToken
        ? await invoices.getPaymentMethodsViaPayToken(payToken, signal)
        : await invoices.getPaymentMethods(signal);
      // Superseded by a newer fetch — return the value but do not clobber state.
      if (myId !== paymentMethodsRequestId) return methods;
      paymentMethods.value = methods;
      paymentMethodsStatus.value = 'success';
      return methods;
    } catch (err) {
      // A cancellation is not a real error — leave state to the superseding
      // request and re-throw for the caller's isCancel swallow.
      if (axios.isCancel(err)) throw err;
      if (myId !== paymentMethodsRequestId) throw err;
      paymentMethodsStatus.value = 'error';
      paymentMethodsError.value = err;
      throw err;
    }
  }

  /**
   * Plan 05.3-02 (D-01/D-06/D-12) — execute a charge against an invoice. Posts
   * the prebuilt transaction-request `body` via the service with `nextPaymentKey`
   * placed as a URL query param (the service owns that). An `isSubmitting` lock
   * prevents a double-submit while a charge is in flight (the server
   * `next_payment_key` is the authoritative idempotency guard — T-05.3-02); a
   * call made while already submitting is rejected without re-posting.
   *
   * The idempotent-replay path (`201` empty body → `{ alreadySucceeded: true }`)
   * is a SUCCESS, not an error — the service maps it; the store just returns it.
   * On a genuine failure (decline / overpay 409 / system error) sets
   * `submitError = err` and re-throws so the modal surfaces `.message`.
   */
  async function submitTransaction(
    invoiceId: number,
    body: TransactionRequestBody,
    nextPaymentKey: string,
    signal?: AbortSignal,
    payToken?: string,
  ): Promise<TransactionResult> {
    if (isSubmitting.value) {
      throw new Error('A payment is already being submitted.');
    }
    isSubmitting.value = true;
    submitError.value = null;
    try {
      const { invoices } = useServices();
      // Plan 09-05 reuse seam: an optional `payToken` routes the charge to the
      // Plan-02 pay-scoped sibling (payClient + ?temp_token=, next_payment_key
      // forwarded verbatim per CR-01); absent, the authed path is byte-unchanged.
      // The isSubmitting lock + idempotent-replay mapping are reused verbatim.
      const result = payToken
        ? await invoices.submitTransactionViaPayToken(
            invoiceId,
            body,
            nextPaymentKey,
            payToken,
            signal,
          )
        : await invoices.submitTransaction(
            invoiceId,
            body,
            nextPaymentKey,
            signal,
          );
      // Gap 1 (nav-badge sync): a successful charge (including an idempotent
      // 201-empty replay) clears/reduces the just-paid invoice's balance, so
      // the badge-driving report is now stale. Refetch FIRE-AND-FORGET so
      // unpaidCount/hasPastDue self-correct without blocking the receipt
      // return. Routed through the store instance (not the local closure) so
      // it goes through the action binding tests spy on. fetchReport()
      // repopulates in place (never clears report[] to []), so the badge does
      // NOT flash to 0 — do NOT use invalidate() here. fetchReport swallows
      // its own errors; the .catch keeps the float lint-clean. The catch
      // branch is deliberately untouched: a FAILED charge must not refetch.
      //
      // Plan 09-05 CRITICAL gate: SKIP the nav-badge refetch on the anonymous
      // pay surface (`payToken` present) — it has no nav badge to sync and
      // fetchReport() would hit the authed apiClient with no session
      // (T-09-05-I1, RESEARCH Seam step 2 / Pitfall 3).
      if (!payToken) {
        useInvoicesStore()
          .fetchReport()
          .catch(() => undefined);
      }
      return result;
    } catch (err) {
      submitError.value = err;
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  }

  /**
   * CR-01 — fetch the invoice's authoritative `next_payment_key` for the pay
   * modal. The server derives it from invoice balance state and embeds it in
   * `payment_create_url`; the adapter parses it onto `InvoiceDetail.nextPaymentKey`.
   * The modal sends it VERBATIM as the charge idempotency key and must never mint
   * its own (a per-attempt UUID defeats the Sera unique-index + GPI/Stax dedup,
   * all keyed on this value — see CR-01).
   *
   * Isolated from `fetchInvoice` on purpose: this does NOT touch the shared
   * `currentInvoice` singleton or detailStatus, so opening the modal from the
   * detail view cannot clobber that view's state. Returns `undefined` when the
   * payload ships no usable key (the caller treats that as "payment unavailable").
   * The caller's `AbortSignal` (the modal's open/re-open controller) is forwarded
   * so a superseded fetch is cancelled rather than racing the winning one.
   */
  async function fetchNextPaymentKey(
    id: number,
    signal?: AbortSignal,
    payToken?: string,
  ): Promise<string | undefined> {
    const { invoices } = useServices();
    // Plan 09-05 reuse seam: an optional `payToken` routes the detail fetch to
    // the Plan-02 pay-scoped getInvoiceViaPayToken (no third pay method — the
    // key re-fetch reuses the invoice fetch); absent, the authed path is
    // byte-unchanged. next_payment_key stays server-authoritative (CR-01).
    const detail = payToken
      ? await invoices.getInvoiceViaPayToken(id, payToken, signal)
      : await invoices.getInvoice(id, signal);
    return detail.nextPaymentKey;
  }

  /**
   * Plan 05.3-02 — reset ONLY the pay-modal payment refs (modal close). The
   * report/detail surfaces above are deliberately untouched.
   */
  function clearPayment(): void {
    paymentMethods.value = [];
    paymentMethodsStatus.value = 'idle';
    paymentMethodsError.value = null;
    isSubmitting.value = false;
    submitError.value = null;
  }

  return {
    // Full-report surface (nav badges, future detail flows)
    report,
    status,
    fetchedAt,
    error,
    unpaidCount,
    unpaidTotalCents,
    hasPastDue,
    fetchReport,
    invalidate,
    reset,

    // Banner surface (UnpaidInvoicesBanner)
    fetchUnpaidInvoices,

    // Paginated history-zone surface (InvoicesView Payment History)
    fetchInvoicePage,

    // Single-invoice detail surface (Plan 05.2-02 — InvoiceDetailView)
    currentInvoice,
    detailStatus,
    detailError,
    fetchInvoice,
    clearCurrent,

    // Pay-modal payment surface (Plan 05.3-02 — PayModal)
    paymentMethods,
    paymentMethodsStatus,
    paymentMethodsError,
    isSubmitting,
    submitError,
    fetchPaymentMethods,
    fetchNextPaymentKey,
    submitTransaction,
    clearPayment,
  };
});
